'use client';

import Typography from '@mui/material/Typography/Typography';
import Box from '@mui/material/Box/Box';
import React, { useEffect, useMemo, useState } from 'react';
import { numberSeparator } from 'number-separator-js';
// import useDebouncedEffect from 'use-debounced-effect'
import Input from '@mui/material/Input';
import { sendGTMEvent } from '@next/third-parties/google';
import { persianNumsToEnglish } from './bank-card-field';
import { IconSvg } from './Icon';
import { PdpInfoDTO } from '@/types/models';
import { useSession } from '@/utils/auth/useSession';
import debounce from '@/utils/debounce';
import { PDPEvents } from '@/misc/event-enums';
interface ProfitCalculatorProps {
  data: PdpInfoDTO;
}
const pushToGTM = debounce(() => {
  sendGTMEvent({
    event: 'pushEvent',
    type: PDPEvents.Calculator
  });
}, 3000);
const ProfitCalculator = ({
  data
}: ProfitCalculatorProps) => {
  const {
    user: userData
  } = useSession();
  const texts = {
    title: 'ماشین‌حساب محاسبه سود',
    currency: 'تومان',
    titleInput: 'سرمایه شما',
    descriptionInput: `بین ${numberSeparator({
      text: (+(userData?.legalType === 'legal' ? data.minLegalInvestment : data.minRealInvestment)).toString()
    })} تومان تا ${numberSeparator({
      text: (+(userData?.legalType === 'legal' ? data.maxLegalInvestment : +data.maxRealInvestment * 10 / 100)).toString()
    })} تومان  وارد کنید`,
    titleResult: 'مجموع سود پیش‌بینی شده',
    descriptionResult: `پرداخت سود هر ${data.profitPerMonth} ماه`
  };
  const [value, setValue] = useState<string>(numberSeparator({
    text: '5000000'
  }));
  const isError = useMemo(() => {
    const inputValue = value.replace(/,/g, '');
    return +inputValue > (userData?.legalType === 'legal' ? +data.maxLegalInvestment : +data.maxRealInvestment);
  }, [value, userData?.legalType]);
  const [result, setResult] = useState('0');
  const [profit, setProfit] = useState('0');
  const onNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    pushToGTM();
    const englishNums = persianNumsToEnglish(e.target.value);
    const parsedNumber = englishNums?.replace(/\s+/g, '')?.replace(/[^0-9]/gi, '');
    const inputValue = parsedNumber.replace(/,/g, '');
    if (!isNaN(Number(inputValue)) && +inputValue < 100_000_000_000) {
      const parsedNumber = numberSeparator({
        text: inputValue
      });
      setValue(parsedNumber);
    }
  };
  useEffect(() => {
    const inputValue = value.replace(/,/g, '');
    if (data && +inputValue < 100_000_000_000) {
      setResult(numberSeparator({
        text: `${(+inputValue * +data.fixedProfit / 100).toFixed(0)}`
      }));
      setProfit(numberSeparator({
        text: `${(+inputValue * +(data.percentPerMonth || 0) / 100).toFixed(0)}`
      }));
    }
  }, [value, data.fixedProfit, data.percentPerMonth]);
  return <Box sx={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    bgcolor: 'background.bg',
    borderRadius: 'var(--corner_radius_m)',
    px: {
      xs: 'var(--spacing_m)',
      md: 'var(--spacing_l_nudge)'
    },
    py: {
      xs: 'var(--spacing_m_nudge)',
      md: 'var(--spacing_m_nudge)'
    },
    my: 'var(--spacing_m)',
    mx: 'auto',
    width: {
      xs: '90%',
      md: '100%'
    }
  }} data-sentry-element="Box" data-sentry-component="ProfitCalculator" data-sentry-source-file="ProfitCalculator.tsx">
      <Typography sx={{
      typography: {
        xs: 'bodyLargeStrong__Wallfund',
        md: 'titleStrong__Wallfund'
      }
    }} component='h4' data-sentry-element="Typography" data-sentry-source-file="ProfitCalculator.tsx">
        {texts.title}
      </Typography>

      <Box sx={{
      display: 'flex',
      width: '100%',
      flexDirection: {
        md: 'row',
        xs: 'column'
      },
      alignItems: 'center',
      pt: 'var(--spacing_m_nudge)'
    }} data-sentry-element="Box" data-sentry-source-file="ProfitCalculator.tsx">
        <Box sx={{
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        flex: 1,
        justifyContent: 'space-between',
        bgcolor: 'background.bg2',
        py: {
          xs: 'var(--spacing_xs)',
          md: 'var(--spacing_xs)'
        },
        borderRadius: 'var(--corner_radius_m)',
        border: '1px solid',
        borderColor: isError ? 'critical.stroke' : 'stroke.stroke4'
      }} data-sentry-element="Box" data-sentry-source-file="ProfitCalculator.tsx">
          <Typography variant='bodyStrong__Wallfund' color='text.text2' sx={{
          px: {
            xs: 'var(--spacing_s)',
            md: 'var(--spacing_s)'
          }
        }} data-sentry-element="Typography" data-sentry-source-file="ProfitCalculator.tsx">
            {texts.titleInput}{' '}
          </Typography>
          <Box sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          py: 'var(--spacing_xs)',
          px: {
            xs: 'var(--spacing_s)',
            md: 'var(--spacing_s)'
          },
          gap: 1,
          borderBottom: '1px solid',
          borderColor: isError ? 'critical.stroke' : 'stroke.stroke4'
        }} data-sentry-element="Box" data-sentry-source-file="ProfitCalculator.tsx">
            <Input type='text' onChange={onNumberChange} value={value} sx={{
            py: 'var(--spacing_xxs)',
            px: {
              xs: 'var(--spacing_s)',
              md: 'var(--spacing_s)'
            }
          }} data-sentry-element="Input" data-sentry-source-file="ProfitCalculator.tsx" />

            <Typography variant='bodyLargeMedium__Wallfund' color='text.text2' data-sentry-element="Typography" data-sentry-source-file="ProfitCalculator.tsx">
              {texts.currency}
            </Typography>
          </Box>

          <Box sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          pt: 'var(--spacing_xxs)',
          px: {
            xs: 'var(--spacing_s)',
            md: 'var(--spacing_s)'
          }
        }} data-sentry-element="Box" data-sentry-source-file="ProfitCalculator.tsx">
            <Typography variant='captionRegular__Wallfund' color={isError ? 'critical.text' : 'text.text2'} data-sentry-element="Typography" data-sentry-source-file="ProfitCalculator.tsx">
              {texts.descriptionInput}
            </Typography>
          </Box>
        </Box>
        <Box sx={{
        transform: {
          xs: 'rotate(-90deg)',
          md: 'none'
        },
        m: {
          xs: 'var(--spacing_m)',
          md: 'var(--spacing_l)'
        }
      }} data-sentry-element="Box" data-sentry-source-file="ProfitCalculator.tsx">
          <IconSvg component={'ArrowtriangleLeftBlack'} width='24' height='24' data-sentry-element="IconSvg" data-sentry-source-file="ProfitCalculator.tsx" />
        </Box>
        <Box sx={{
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        flex: 1,
        justifyContent: 'space-between',
        bgcolor: 'approve.bg4',
        py: {
          xs: 'var(--spacing_xs)',
          md: 'var(--spacing_xs)'
        },
        borderRadius: 'var(--corner_radius_m)',
        border: '1px solid',
        borderColor: 'approve.stroke2'
      }} data-sentry-element="Box" data-sentry-source-file="ProfitCalculator.tsx">
          <Typography variant='bodyStrong__Wallfund' color='approve.text' sx={{
          px: {
            xs: 'var(--spacing_s)',
            md: 'var(--spacing_s)'
          }
        }} data-sentry-element="Typography" data-sentry-source-file="ProfitCalculator.tsx">
            {texts.titleResult}
          </Typography>
          <Box sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          py: 'var(--spacing_xs)',
          px: {
            xs: 'var(--spacing_s)',
            md: 'var(--spacing_s)'
          },
          gap: 1,
          borderBottom: '1px solid',
          borderColor: 'approve.stroke2'
        }} data-sentry-element="Box" data-sentry-source-file="ProfitCalculator.tsx">
            <Typography variant='displayStrong__Wallfund' color='approve.text' data-sentry-element="Typography" data-sentry-source-file="ProfitCalculator.tsx">
              {result}
            </Typography>
            <Typography variant='bodyLargeMedium__Wallfund' color='approve.text' data-sentry-element="Typography" data-sentry-source-file="ProfitCalculator.tsx">
              {texts.currency}
            </Typography>
          </Box>
          <Box sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          pt: 'var(--spacing_xxs)',
          px: {
            xs: 'var(--spacing_s)',
            md: 'var(--spacing_s)'
          }
        }} data-sentry-element="Box" data-sentry-source-file="ProfitCalculator.tsx">
            <Typography variant='captionRegular__Wallfund' color='approve.text' data-sentry-element="Typography" data-sentry-source-file="ProfitCalculator.tsx">
              {texts.descriptionResult} {profit} تومان
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>;
};
export default ProfitCalculator;